import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, CheckCircle2, Loader2 } from 'lucide-react';

export interface ReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isBusy: boolean;
  data: {
    squadraNome?: string;
    collaboratori?: string;
    mezzoTarga?: string;
    autista?: string;
    impiantoCodice?: string;
    impiantoNome?: string;
    impiantoTipo?: string;
    statoImpiantoArrivo: string;
    causaGuasto: string;
    azioniEseguite: string;
    inizioLavoroAt?: string;
    fineLavoroAt?: string;
    materiali?: Array<{ nome: string; quantita?: string }>;
    esitoLabel: string;
    statoDestinazione?: string;
  };
}

export function ReviewModal({ isOpen, onClose, onConfirm, data, isBusy }: ReviewModalProps) {
  useEffect(() => {
    if (!isOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  if (!isOpen) return null;
  if (typeof document === 'undefined') return null;

  const formatTime = (iso?: string) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return iso;
    }
  };

  const isChiusa = data.statoDestinazione !== 'sospesa';

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-xs animate-fade-in safe-bottom">
      <div className="w-full max-w-lg rounded-2xl sm:rounded-3xl bg-white shadow-2xl flex flex-col max-h-[90dvh] sm:max-h-[85vh] overflow-hidden animate-slide-up border border-slate-200">
        {/* Header Modale Fisso */}
        <div className="p-4 sm:p-5 border-b border-slate-100 flex items-center justify-between shrink-0 bg-white">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center font-bold text-sm">
              📋
            </div>
            <div>
              <h3 className="text-base sm:text-lg font-extrabold text-slate-900 leading-tight">
                Revisione Report Finale
              </h3>
              <p className="text-xs text-slate-500">
                Verifica i dati inseriti prima dell'invio definitivo
              </p>
            </div>
          </div>
          <button
            type="button"
            className="btn-ghost p-1.5 text-slate-400 hover:text-slate-600 rounded-xl"
            onClick={onClose}
            disabled={isBusy}
            title="Chiudi e torna alla modifica"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Corpo Scrollabile con i dati compilati */}
        <div className="p-4 sm:p-5 overflow-y-auto flex-1 space-y-3 text-xs sm:text-sm overscroll-contain">
          {/* Impianto */}
          {(data.impiantoCodice || data.impiantoNome) && (
            <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-200/80 space-y-1">
              <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider block">
                🏢 Impianto Semaforico
              </span>
              <div className="font-extrabold text-slate-900 text-sm sm:text-base">
                {data.impiantoCodice ? `Imp. ${data.impiantoCodice}` : ''} {data.impiantoNome ? `· ${data.impiantoNome}` : ''}
              </div>
              {data.impiantoTipo && (
                <div className="text-xs text-slate-500 font-medium">Tipologia: {data.impiantoTipo}</div>
              )}
            </div>
          )}

          {/* Operatori Intervento, Mezzo e Guida */}
          <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-200/80 space-y-2">
            <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider block">
              👷‍♂️ Squadra, Collaboratori e Mezzo
            </span>
            <div className="font-extrabold text-slate-900 text-sm">
              {data.squadraNome || 'La tua squadra'}
              {data.collaboratori?.trim() ? (
                <span className="text-blue-700"> + {data.collaboratori.trim()}</span>
              ) : (
                <span className="text-slate-500 font-medium"> (Nessun collaboratore / Da solo)</span>
              )}
            </div>
            {(data.mezzoTarga || data.autista) && (
              <div className="flex flex-wrap items-center gap-2 text-xs pt-1 border-t border-slate-200/60 text-slate-700">
                {data.mezzoTarga && (
                  <span className="font-semibold bg-white px-2 py-0.5 rounded-md border border-slate-200">
                    🚗 Mezzo: <strong>{data.mezzoTarga}</strong>
                  </span>
                )}
                {data.autista && (
                  <span className="font-semibold bg-white px-2 py-0.5 rounded-md border border-slate-200">
                    👤 Guida: <strong>{data.autista}</strong>
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Diagnosi: Stato arrivo & Causa guasto */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            <div className="p-3 rounded-2xl bg-slate-50 border border-slate-200/80 space-y-0.5">
              <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider block">
                🚥 Stato Impianto all'Arrivo
              </span>
              <div className="font-extrabold text-slate-900">
                {data.statoImpiantoArrivo || '—'}
              </div>
            </div>

            <div className="p-3 rounded-2xl bg-amber-50/70 border border-amber-200/80 space-y-0.5">
              <span className="text-[10px] font-extrabold text-amber-800 uppercase tracking-wider block">
                ⚠️ Causa Guasto Riscontrata
              </span>
              <div className="font-extrabold text-amber-950">
                {data.causaGuasto || '—'}
              </div>
            </div>
          </div>

          {/* Azioni eseguite */}
          <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-200/80 space-y-1">
            <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider block">
              📝 Intervento & Azioni Eseguite
            </span>
            <div className="font-medium text-slate-800 leading-relaxed italic">
              {data.azioniEseguite.trim() ? `"${data.azioniEseguite.trim()}"` : 'Nessuna descrizione specificata'}
            </div>
          </div>

          {/* Orari di lavorazione */}
          <div className="p-3 rounded-2xl bg-slate-50 border border-slate-200/80 space-y-1">
            <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider block">
              🕒 Orario Lavorazione sul Posto
            </span>
            <div className="flex items-center gap-4 text-xs font-bold text-slate-900">
              <div>Inizio: <span className="text-emerald-700 font-extrabold">{formatTime(data.inizioLavoroAt)}</span></div>
              <div>Fine: <span className="text-emerald-700 font-extrabold">{formatTime(data.fineLavoroAt)}</span></div>
            </div>
          </div>

          {/* Materiali impiegati */}
          <div className="p-3 rounded-2xl bg-slate-50 border border-slate-200/80 space-y-1.5">
            <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider block">
              🧰 Materiali & Ricambi Impiegati
            </span>
            {data.materiali && data.materiali.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {data.materiali.map((m, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center px-2.5 py-1 rounded-xl bg-white border border-slate-200 text-xs font-bold text-slate-800 shadow-2xs"
                  >
                    • {m.nome}{m.quantita && String(m.quantita).trim() !== '' ? ` (${m.quantita})` : ''}
                  </span>
                ))}
              </div>
            ) : (
              <div className="text-xs text-slate-500 italic">Nessun materiale o ricambio registrato</div>
            )}
          </div>

          {/* Esito tecnico & Destinazione */}
          <div className={`rounded-2xl border p-3.5 space-y-1 ${isChiusa ? 'bg-emerald-50 border-emerald-200 text-emerald-950' : 'bg-amber-50 border-amber-200 text-amber-950'}`}>
            <div className="font-extrabold flex items-center gap-1.5 text-xs sm:text-sm">
              <CheckCircle2 className={`w-4 h-4 ${isChiusa ? 'text-emerald-600' : 'text-amber-600'}`} />
              Esito: {data.esitoLabel} ({isChiusa ? 'Chiusura Definitiva' : 'Messa in Da Terminare'})
            </div>
            <p className="text-xs leading-relaxed opacity-90">
              {isChiusa
                ? 'L\'intervento verrà registrato come completato e la chiamata verrà CHIUSA.'
                : 'L\'intervento verrà registrato come parziale e la chiamata resterà in stato DA TERMINARE.'}
            </p>
          </div>
        </div>

        {/* Footer Fisso Sempre Visibile con i Pulsanti di Azione */}
        <div className="p-3.5 sm:p-4 bg-slate-50 border-t border-slate-200/90 shrink-0 grid grid-cols-2 gap-2.5 z-10">
          <button
            type="button"
            className="btn-secondary w-full justify-center text-xs sm:text-sm font-bold min-h-[44px]"
            disabled={isBusy}
            onClick={onClose}
          >
            ← Modifica dati
          </button>
          <button
            type="button"
            className="btn-primary w-full justify-center text-xs sm:text-sm font-extrabold gap-1.5 shadow-md min-h-[44px] bg-emerald-600 hover:bg-emerald-700 border-emerald-600"
            disabled={isBusy}
            onClick={onConfirm}
          >
            {isBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            <span>Conferma e Invia</span>
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
